import { API_BASE, getAccessToken } from "@/lib/api/session";
import { t } from "@/i18n";

export type PriceDisplay = {
  plan: "plus_monthly" | "plus_yearly";
  amount: number;
  currency: string;
  label: string;
  interval: "month" | "year";
};

export type BillingStatus = {
  plan: string;
  status: string;
  isPremium: boolean;
  currentPeriodEnd: string | null;
  entitlements: Array<{
    entitlement_code: string;
    active: boolean;
    expires_at: string | null;
  }>;
  checkoutAvailable: boolean;
  prices?: {
    monthly: PriceDisplay | null;
    yearly: PriceDisplay | null;
    yearlySavePercent: number | null;
  };
};

export type PaywallReason =
  | "default"
  | "coach"
  | "experiment"
  | "programs"
  | "explore";

export async function fetchBillingStatus(): Promise<BillingStatus | null> {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/api/v1/billing/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as BillingStatus;
  } catch {
    return null;
  }
}

export async function startCheckout(
  plan: "plus_monthly" | "plus_yearly" = "plus_monthly"
): Promise<{ ok: true; url: string } | { ok: false; message: string }> {
  const token = await getAccessToken();
  if (!token) {
    return { ok: false, message: t("errors.loginForUpgrade") };
  }
  try {
    const res = await fetch(`${API_BASE}/api/v1/billing/checkout`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ plan }),
    });
    const json = (await res.json()) as { url?: string; message?: string };
    if (!res.ok || !json.url) {
      return {
        ok: false,
        message: json.message ?? "Checkout är inte tillgänglig ännu.",
      };
    }
    return { ok: true, url: json.url };
  } catch {
    return { ok: false, message: t("errors.checkoutFail") };
  }
}
